'use client';

import React, { useState } from 'react';
import { X, Building2, MapPin } from 'lucide-react';
import { Farm, FarmType, FarmStatus, FARM_TYPE_LABELS } from './farmsData';

interface CreateFarmModalProps {
  onClose: () => void;
  onSave: (data: Partial<Farm>) => void;
}

const FARM_TYPES: FarmType[] = ['broiler', 'layer', 'breeder', 'indigenous', 'mixed'];
const STATUSES: FarmStatus[] = ['active', 'inactive'];

export default function CreateFarmModal({ onClose, onSave }: CreateFarmModalProps) {
  const [form, setForm] = useState({
    farmName: '',
    farmCode: '',
    farmType: 'mixed' as FarmType,
    owner: '',
    manager: '',
    managerPhone: '',
    managerEmail: '',
    country: 'Uganda',
    district: '',
    subcounty: '',
    village: '',
    gpsLat: '',
    gpsLng: '',
    phone: '',
    email: '',
    openingDate: '',
    totalCapacity: '',
    status: 'active' as FarmStatus,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate() {
    const e: Record<string, string> = {};
    if (!form.farmName.trim()) e.farmName = 'Farm name is required';
    if (!form.farmCode.trim()) e.farmCode = 'Farm code is required';
    if (!form.manager.trim()) e.manager = 'Manager name is required';
    if (!form.district.trim()) e.district = 'District is required';
    if (!form.totalCapacity || isNaN(Number(form.totalCapacity))) e.totalCapacity = 'Valid capacity required';
    return e;
  }

  function handleChange(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => { const n = { ...e }; delete n[field]; return n; });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    setTimeout(() => {
      onSave({ ...form, totalCapacity: Number(form.totalCapacity), branches: [], currentBirds: 0, activeHouses: 0, activeFlocks: 0, totalEmployees: 0, monthlyRevenue: 0, monthlyExpenses: 0, eggsTodayCount: 0, mortalityToday: 0, feedInStock: 0 });
      setSaving(false);
    }, 600);
  }

  const Field = ({ label, field, type = 'text', required = false, placeholder = '' }: { label: string; field: string; type?: string; required?: boolean; placeholder?: string }) => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      <input
        type={type}
        value={(form as Record<string, string>)[field]}
        onChange={e => handleChange(field, e.target.value)}
        placeholder={placeholder}
        className={`input-field ${errors[field] ? 'border-red-400' : ''}`}
      />
      {errors[field] && <p className="text-xs text-red-500 mt-0.5">{errors[field]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <Building2 size={16} className="text-green-700" />
            </div>
            <h2 className="text-base font-bold text-gray-900">Add New Farm</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5"><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto scrollbar-thin p-6 space-y-5">
          {/* Basic Info */}
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Farm Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Farm Name" field="farmName" required placeholder="e.g. Idudi Poultry Farm" />
              <Field label="Farm Code" field="farmCode" required placeholder="e.g. IDF-001" />
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Farm Type<span className="text-red-500 ml-0.5">*</span></label>
                <select className="select-field" value={form.farmType} onChange={e => handleChange('farmType', e.target.value)}>
                  {FARM_TYPES.map(t => <option key={t} value={t}>{FARM_TYPE_LABELS[t]}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                <select className="select-field" value={form.status} onChange={e => handleChange('status', e.target.value)}>
                  {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <Field label="Owner" field="owner" placeholder="e.g. Mr. John Okello" />
              <Field label="Total Capacity (birds)" field="totalCapacity" type="number" required placeholder="e.g. 10000" />
              <Field label="Opening Date" field="openingDate" type="date" />
              <Field label="Phone" field="phone" placeholder="+256 414 000 000" />
              <div className="md:col-span-2">
                <Field label="Email" field="email" type="email" placeholder="farm@example.com" />
              </div>
            </div>
          </div>

          {/* Manager */}
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Farm Manager</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Manager Name" field="manager" required placeholder="Full name" />
              <Field label="Manager Phone" field="managerPhone" placeholder="+256 772 000 000" />
              <Field label="Manager Email" field="managerEmail" type="email" placeholder="manager@example.com" />
            </div>
          </div>

          {/* Location */}
          <div>
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <MapPin size={12} />Location
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Country" field="country" placeholder="Uganda" />
              <Field label="District" field="district" required placeholder="e.g. Iganga" />
              <Field label="Subcounty" field="subcounty" placeholder="e.g. Idudi" />
              <Field label="Village" field="village" placeholder="e.g. Nabitende" />
              <Field label="GPS Latitude" field="gpsLat" placeholder="e.g. 0.4521" />
              <Field label="GPS Longitude" field="gpsLng" placeholder="e.g. 33.4812" />
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? 'Saving…' : 'Save Farm'}
          </button>
        </div>
      </div>
    </div>
  );
}
